/**
 * The notebook's lock, browser side (§4.12; the lock itself moved onto the
 * account 2026-09-07 拍板).
 *
 * What is left here. The password is the server's now — set, verified and
 * reset over /api/me/notebook-lock (#edge/ember/notebook-lock.js), so a
 * notebook closed on a phone is closed on a laptop too, and forgetting it has
 * an answer that is not "take the lock off". This module keeps the two things
 * that are genuinely per-browser:
 *
 *   1. Whether this browser session has already opened the notebook, so the
 *      ceremony runs once an afternoon rather than on every route change.
 *   2. The LEGACY lock — the SHA-256 that lived in localStorage between
 *      2026-09-04 and today. A person who set one is still standing behind it
 *      in this browser, and dropping it silently would open their notebook on
 *      a shared phone without telling them. So it is still honoured: the cover
 *      asks, this file verifies, and a correct password is handed to the
 *      server as the account's lock and erased from here. A legacy lock keeps
 *      the old no-password escape hatch, because it never existed anywhere we
 *      could text a link to.
 *
 * Storage is per-browser and may be unavailable (private windows, blocked site
 * data, thumbnailers). Everything here is wrapped, and an unreadable store
 * reads as "no legacy lock": being unable to remember the key is not a reason
 * to shut someone out of their own page.
 */

import { GOOGLE_CLIENT_ID } from '../../../../shared-web/google-signin';
import { isInAppWebview } from '../../../../shared-web/ua-detect';

const LOCK_KEY = 'ember.notebook.lock';
/** Cleared by the browser when the tab session ends — hence "once a session". */
const SESSION_KEY = 'ember.notebook.opened';

/** The lock as the page holds it: the server's answer, plus the legacy one. */
export interface NotebookLockState {
  /** The account's lock — the one that follows the person between browsers. */
  locked: boolean;
  /** A pre-2026-09-07 lock still sitting in THIS browser, not yet migrated. */
  legacy: LegacyLock | null;
  /** Can a way back in be texted, and to which number (masked). */
  recovery: { available: boolean; hint: string; reason: string };
  /** Confirm it's you (2026-10-19): the account's own sign-in methods, and the masked number a code goes to. */
  reauth: { methods: string[]; phone_hint: string };
}

export interface LegacyLock { salt: string; hash: string }

export const NO_RECOVERY = { available: false, hint: '', reason: '' };
export const NO_REAUTH = { methods: [] as string[], phone_hint: '' };

/**
 * The account's methods this browser can run (2026-10-19): a phone code always; Google when the web has its door
 * and is not inside an app's webview (Google refuses those, as Settings knows); Apple never — the web has none.
 */
export function webReauthMethods(state: NotebookLockState): Array<'google' | 'phone'> {
  const googleReady = Boolean(GOOGLE_CLIENT_ID) && !isInAppWebview();
  const have = new Set(state.reauth?.methods || []);
  return [
    ...(have.has('google') && googleReady ? ['google' as const] : []),
    ...(have.has('phone') ? ['phone' as const] : []),
  ];
}

function readStore(key: string, store: 'local' | 'session'): string | null {
  try {
    return (store === 'local' ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string, store: 'local' | 'session'): void {
  try {
    (store === 'local' ? window.localStorage : window.sessionStorage).setItem(key, value);
  } catch { /* the ceremony survives an unwritable store; the lock does not */ }
}

/** The pre-2026-09-07 lock, or null when this browser never held one. */
export function readLegacyLock(): LegacyLock | null {
  const raw = readStore(LOCK_KEY, 'local');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { kind?: string; salt?: string; hash?: string };
    if (parsed.kind === 'password' && parsed.salt && parsed.hash) {
      return { salt: parsed.salt, hash: parsed.hash };
    }
  } catch { /* a corrupt record is the same as never having set one */ }
  return null;
}

export function removeLegacyLock(): void {
  try {
    window.localStorage.removeItem(LOCK_KEY);
  } catch { /* nothing stored, nothing to remove */ }
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 over salt + password — the legacy digest, kept only to verify. */
async function digest(salt: string, password: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}\n${password}`);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return toHex(new Uint8Array(buf));
}

export async function verifyLegacyPassword(lock: LegacyLock, password: string): Promise<boolean> {
  return (await digest(lock.salt, password)) === lock.hash;
}

/** Whether the notebook has already been opened in this browser session. */
export function openedThisSession(): boolean {
  return readStore(SESSION_KEY, 'session') === '1';
}

export function markOpenedThisSession(): void {
  writeStore(SESSION_KEY, '1', 'session');
}

/**
 * Closing the notebook (2026-09-05): the session's "already opened" is
 * forgotten, so the next arrival at /me meets the cover again — and, since a
 * notebook is only ever closed with a password on it, the ask beneath it.
 */
export function markClosed(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch { /* nothing remembered, nothing to forget */ }
}
